export enum OvertimePeriodOption {
    None = "none",
    Period = "period",
    Month = "month",
    Week = "week",
}

export enum OvertimeDailyOption {
    Daily = "daily",
    Saturday = "saturday",
}

export enum LunchBreakOption {
    None = "none",
    Deduction = "deduction",
    Paid = "paid",
}

export enum ShiftEffectiveDateLogic {
    StartDate = 0, // Default
    MostHours = 1,
}

export interface IBoundaries {
    boundary: number;
    boundaryValue: number;
}

export class TESCalculationParameters {
    isPaidLunchBreakOn = false;
    lunchBreakOption = LunchBreakOption.None;
    dailyBreakDuration = 0;
    stdShiftDuration = 0;
    isWorkShiftStartBasedOnPlannedShift = false;
    dayWorkStartTime = "06:00";
    eveningWorkStartTime = "18:00";
    nightWorkStartTime = "22:00";
    paidBreak = 0;
    paidBreakShiftMinMinutes = 0;
    paidWaiting = 0;
    isEveningAndNightAdditionThresholdOn = false;
    eveningAndNightAdditionThreshold = 12;
    isOvertimeCalculationOn = false;
    isEveningCalculationOn = false;
    isSaturdayEveningCalculationOn = true;
    isSundayEveningCalculationOn = true;
    isSundayOrHolidaySpecificEveningCalculationOn = false;
    sundaySpecificEveningWorkStartTime = "18:00";
    sundaySpecificEveningWorkEndTime = "22:00";
    isNightCalculationOn = false;
    isSundayNightCalculationOn = true;
    isSundayOrHolidaySpecificNightCalculationOn = false;
    sundaySpecificNightWorkStartTime = "22:00";
    sundaySpecificNightWorkEndTime = "06:00";
    isSundayCalculationOn = false;
    isSaturdayCalculationOn = false;
    saturdayStartTime = "00:00";
    saturdayEndTime = "00:00";
    isGrandHolidayDayCalculationOn = false;
    overtimePeriodBoundaries: IBoundaries[] = [];
    isMonthlyOvertimeCalculationOn = false;
    isWeeklyOvertimeCalculationOn = false;
    overtimeWeekBoundaries: IBoundaries[] = [];
    overtimePeriodOption = OvertimePeriodOption.None;
    isDailyOvertimeCalculationOn = false;
    isSaturdayOvertimeCalculationOn = false;
    overtimeDailyBoundaries: IBoundaries[] = [];
    overtimeSaturdayBoundaries: IBoundaries[] = [];
    midweekHolidayOvertimeLimitDeduction = 0;
    grandHolidayEveStartTime = "14:00";
    resultRoundingMinutes = 0;
    isResultRoundingMinutesOn = false;
    shiftRoundingMinutes = 0;
    isShiftRoundingMinutesOn = false;
    shiftEffectiveDateLogic = ShiftEffectiveDateLogic.StartDate;
    dailyAllowanceBoundaries: IBoundaries[] = [];
    isFillingHourCalculationOn = false;
    fillingHourCalculationLimit = 80;
    isFixedAdditionOn = false;
    isVehicleGroupAdditionOn = false;
    isDailyAllowanceCostCenterIdOn = false;
    isDailyAllowanceVehicleIdOn = false;
    isIncreasedDailyAllowanceOn = false;
    increasedDailyAllowanceBoundary = 24;
    isIncreasedDailyAllowanceBasedOnOvernightStay = false;

    constructor();
    constructor(obj: TESCalculationParameters);
    constructor(obj?: any) {
        if (obj) {
            this.lunchBreakOption =
                obj.dailyBreakDuration === 0
                    ? LunchBreakOption.None
                    : obj.isPaidLunchBreakOn
                        ? LunchBreakOption.Paid
                        : LunchBreakOption.Deduction;
            this.isPaidLunchBreakOn = obj.isPaidLunchBreakOn;
            this.dailyBreakDuration = obj.dailyBreakDuration;
            this.stdShiftDuration = obj.stdShiftDuration;
            this.isWorkShiftStartBasedOnPlannedShift =
                obj.isWorkShiftStartBasedOnPlannedShift ?? false;
            this.dayWorkStartTime = obj.dayWorkStartTime;
            this.eveningWorkStartTime = obj.eveningWorkStartTime;
            this.nightWorkStartTime = obj.nightWorkStartTime;
            this.paidBreak = obj.paidBreak;
            this.paidBreakShiftMinMinutes = obj.paidBreakShiftMinMinutes ?? 0;
            this.paidWaiting = obj.paidWaiting;
            this.isEveningAndNightAdditionThresholdOn = obj.isEveningAndNightAdditionThresholdOn ?? false;
            this.eveningAndNightAdditionThreshold =
                obj.eveningAndNightAdditionThreshold ?? 12;
            this.isSundayCalculationOn = obj.isSundayCalculationOn;
            this.isSaturdayCalculationOn = obj.isSaturdayCalculationOn;
            this.saturdayStartTime = obj.saturdayStartTime ?? "00:00";
            this.saturdayEndTime = obj.saturdayEndTime ?? "00:00";
            this.isGrandHolidayDayCalculationOn =
                obj.isGrandHolidayDayCalculationOn ||
                obj.isGrandHolidayPeriodCalculationOn;
            this.isEveningCalculationOn = obj.isEveningCalculationOn;
            this.isSaturdayEveningCalculationOn =
                obj.isSaturdayEveningCalculationOn ?? true;
            this.isSundayEveningCalculationOn =
                obj.isSundayEveningCalculationOn ?? true;
            this.isSundayOrHolidaySpecificEveningCalculationOn =
                obj.isSundayOrHolidaySpecificEveningCalculationOn ?? false;
            this.sundaySpecificEveningWorkStartTime =
                obj.sundaySpecificEveningWorkStartTime ?? "18:00";
            this.sundaySpecificEveningWorkEndTime =
                obj.sundaySpecificEveningWorkEndTime ?? "22:00";
            this.isNightCalculationOn = obj.isNightCalculationOn;
            this.isSundayNightCalculationOn =
                obj.isSundayNightCalculationOn ?? true;
            this.isSundayOrHolidaySpecificNightCalculationOn =
                obj.isSundayOrHolidaySpecificNightCalculationOn ?? false;
            this.sundaySpecificNightWorkStartTime =
                obj.sundaySpecificNightWorkStartTime ?? "22:00";
            this.sundaySpecificNightWorkEndTime =
                obj.sundaySpecificNightWorkEndTime ?? "06:00";
            this.isOvertimeCalculationOn = obj.isOvertimeCalculationOn;
            this.overtimePeriodBoundaries = obj.overtimePeriodBoundaries
                ? obj.overtimePeriodBoundaries
                : [];
            this.isMonthlyOvertimeCalculationOn =
                obj.isMonthlyOvertimeCalculationOn;
            this.isWeeklyOvertimeCalculationOn =
                obj.isWeeklyOvertimeCalculationOn;
            this.overtimeWeekBoundaries = obj.overtimeWeekBoundaries
                ? obj.overtimeWeekBoundaries
                : [];
            this.overtimePeriodOption = obj.isMonthlyOvertimeCalculationOn
                ? OvertimePeriodOption.Month
                : this.isWeeklyOvertimeCalculationOn
                    ? OvertimePeriodOption.Week
                    : this.isOvertimeCalculationOn
                        ? OvertimePeriodOption.Period
                        : OvertimePeriodOption.None;
            this.isDailyOvertimeCalculationOn =
                obj.isDailyOvertimeCalculationOn;
            this.isSaturdayOvertimeCalculationOn =
                obj.isSaturdayOvertimeCalculationOn;
            this.overtimeDailyBoundaries = obj.overtimeDailyBoundaries
                ? obj.overtimeDailyBoundaries
                : [];
            this.overtimeSaturdayBoundaries = obj.overtimeSaturdayBoundaries
                ? obj.overtimeSaturdayBoundaries
                : [];
            this.midweekHolidayOvertimeLimitDeduction =
                obj.midweekHolidayOvertimeLimitDeduction ?? 0;
            this.grandHolidayEveStartTime = obj.grandHolidayEveStartTime;
            this.resultRoundingMinutes = obj.resultRoundingMinutes || 30;
            this.isResultRoundingMinutesOn = obj.isResultRoundingMinutesOn;
            this.shiftRoundingMinutes = obj.shiftRoundingMinutes || 30;
            this.isShiftRoundingMinutesOn = obj.isShiftRoundingMinutesOn;
            this.shiftEffectiveDateLogic =
                obj.shiftEffectiveDateLogic ||
                ShiftEffectiveDateLogic.StartDate;
            this.dailyAllowanceBoundaries = obj.dailyAllowanceBoundaries || [];
            this.isFillingHourCalculationOn = obj.isFillingHourCalculationOn;
            this.fillingHourCalculationLimit =
                obj.fillingHourCalculationLimit ?? 80;
            this.isFixedAdditionOn = obj.isFixedAdditionOn;
            this.isVehicleGroupAdditionOn = obj.isVehicleGroupAdditionOn;
            this.isDailyAllowanceCostCenterIdOn = obj.isDailyAllowanceCostCenterIdOn;
            this.isDailyAllowanceVehicleIdOn = obj.isDailyAllowanceVehicleIdOn;
            this.isIncreasedDailyAllowanceOn = obj.isIncreasedDailyAllowanceOn;
            this.increasedDailyAllowanceBoundary =
                obj.increasedDailyAllowanceBoundary ?? 24;
            this.isIncreasedDailyAllowanceBasedOnOvernightStay =
                obj.isIncreasedDailyAllowanceBasedOnOvernightStay;
        }
    }
}
